import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, 
  Store, 
  CheckCircle, 
  XCircle, 
  Clock,
  Package,
  Trash2,
  FileText,
  Truck,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const NASShopOrders = ({ embedded = false }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Forms
  const [orderForm, setOrderForm] = useState({
    supplierName: '',
    orderDate: new Date().toISOString().split('T')[0],
    expectedDelivery: '',
    items: [],
    paymentTerms: 'cod',
    notes: ''
  });
  
  const [newItem, setNewItem] = useState({
    productId: '',
    productName: '',
    quantity: '',
    unit: 'sack',
    unitPrice: ''
  });
  
  const [receiveForm, setReceiveForm] = useState({
    receivedItems: [],
    qualityNotes: '',
    receivedBy: ''
  });
  
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentMethod: 'cash',
    checkNumber: '',
    checkDate: '',
    notes: ''
  });
  
  const [rejectReason, setRejectReason] = useState('');

  // Access check
  const username = user?.username?.toLowerCase() || '';
  const fullName = user?.full_name?.toLowerCase() || '';
  const isAdmin = user?.role === 'admin';
  
  // NAS Shop Orders: Nicole, Kristine, Geraldine, Cristel
  const canCreateOrders = isAdmin || ['nicole', 'kristine', 'geraldine', 'cristel'].some(n => username.includes(n) || fullName.includes(n));
  
  // Order Approval: Nicole, Arbe, Pabs
  const canApproveOrders = isAdmin || ['nicole', 'arbe', 'pabs'].some(n => username.includes(n) || fullName.includes(n));
  
  // Receiving: Geraldine, Cristel, Nicole
  const canReceiveOrders = isAdmin || ['geraldine', 'cristel', 'nicole'].some(n => username.includes(n) || fullName.includes(n));
  
  // Payment/Cheque: Alma, Nicole, Kristine
  const canManagePayment = isAdmin || ['alma', 'nicole', 'kristine'].some(n => username.includes(n) || fullName.includes(n));

  useEffect(() => {
    loadOrders();
    loadProducts();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/nas/shop/supplier-orders`);
      setOrders(res.data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
      // Initialize empty if endpoint doesn't exist yet
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const res = await axios.get(`${API}/api/nas/products`);
      setProducts(res.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', { 
      year: 'numeric', month: 'short', day: 'numeric' 
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_approval': return 'bg-amber-100 text-amber-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'pending_payment': return 'bg-purple-100 text-purple-700';
      case 'paid': return 'bg-cyan-100 text-cyan-700';
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'received': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending_approval': return 'Pending Approval';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'pending_payment': return 'Awaiting Payment';
      case 'paid': return 'Paid';
      case 'delivered': return 'Out for Delivery';
      case 'received': return 'Received';
      default: return status;
    }
  };

  // Add item to order
  const addItemToOrder = () => {
    if (!newItem.productName || !newItem.quantity || !newItem.unitPrice) {
      toast.error('Please fill in product name, quantity, and unit price');
      return;
    }
    
    setOrderForm({
      ...orderForm,
      items: [...orderForm.items, {
        ...newItem,
        quantity: parseFloat(newItem.quantity),
        unitPrice: parseFloat(newItem.unitPrice),
        totalPrice: parseFloat(newItem.quantity) * parseFloat(newItem.unitPrice)
      }]
    });
    
    setNewItem({ productId: '', productName: '', quantity: '', unit: 'sack', unitPrice: '' });
  };

  const removeItemFromOrder = (index) => {
    setOrderForm({
      ...orderForm,
      items: orderForm.items.filter((_, i) => i !== index)
    });
  };

  const calculateOrderTotal = () => {
    return orderForm.items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
  };

  // Create order
  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (!orderForm.supplierName || orderForm.items.length === 0) {
      toast.error('Please fill in supplier name and add at least one item');
      return;
    }

    try {
      await axios.post(`${API}/api/nas/shop/supplier-orders`, {
        ...orderForm,
        totalAmount: calculateOrderTotal()
      });
      toast.success('Shop supplier order created successfully');
      setShowCreateDialog(false);
      setOrderForm({
        supplierName: '', orderDate: new Date().toISOString().split('T')[0],
        expectedDelivery: '', items: [], paymentTerms: 'cod', notes: ''
      });
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  // Approve order
  const handleApproveOrder = async (orderId) => {
    try {
      await axios.put(`${API}/api/nas/shop/supplier-orders/${orderId}/approve`);
      toast.success('Order approved - Ready for payment');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve order');
    }
  };

  // Reject order
  const handleRejectOrder = async () => {
    if (!selectedOrder) return;
    
    try {
      await axios.put(`${API}/api/nas/shop/supplier-orders/${selectedOrder.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Order rejected');
      setShowRejectDialog(false);
      setSelectedOrder(null);
      setRejectReason('');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject order');
    }
  };

  // Create payment
  const handleCreatePayment = async () => {
    if (!selectedOrder || !paymentForm.amount) {
      toast.error('Please enter payment amount');
      return;
    }
    
    try {
      await axios.post(`${API}/api/nas/shop/supplier-orders/${selectedOrder.id}/payment`, paymentForm);
      toast.success('Payment recorded');
      setShowPaymentDialog(false);
      setSelectedOrder(null);
      setPaymentForm({ amount: '', paymentMethod: 'cash', checkNumber: '', checkDate: '', notes: '' });
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  // Receive order
  const handleReceiveOrder = async () => {
    if (!selectedOrder) return;
    
    try {
      await axios.put(`${API}/api/nas/shop/supplier-orders/${selectedOrder.id}/receive`, {
        qualityNotes: receiveForm.qualityNotes
      });
      toast.success('Order received! Items added to NAS Shop inventory.');
      setShowReceiveDialog(false);
      setSelectedOrder(null);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive order');
    }
  };

  // Filter orders by status
  const filteredOrders = orders.filter(order => {
    if (activeTab === 'all') return true;
    return order.status === activeTab;
  });

  // Count by status
  const counts = {
    all: orders.length,
    pending_approval: orders.filter(o => o.status === 'pending_approval').length,
    approved: orders.filter(o => o.status === 'approved').length,
    pending_payment: orders.filter(o => o.status === 'pending_payment').length,
    received: orders.filter(o => o.status === 'received').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-4" : "space-y-6"}>
      {/* Header */}
      {!embedded && (
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Store className="text-emerald-600" size={28} />
              NAS Shop Supplier Orders
            </h1>
            <p className="text-slate-600">Order products directly for NAS Shop inventory</p>
          </div>
          {canCreateOrders && (
            <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus size={18} className="mr-2" />
              Create Order
            </Button>
          )}
        </div>
      )}
      
      {/* Create button when embedded */}
      {embedded && canCreateOrders && (
        <div className="flex justify-end">
          <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus size={18} className="mr-2" />
            Create Shop Order
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('all')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-slate-800">{counts.all}</p>
            <p className="text-sm text-slate-500">All Orders</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md bg-amber-50" onClick={() => setActiveTab('pending_approval')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{counts.pending_approval}</p>
            <p className="text-sm text-amber-700">Pending</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md bg-blue-50" onClick={() => setActiveTab('approved')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{counts.approved}</p>
            <p className="text-sm text-blue-700">Approved</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md bg-purple-50" onClick={() => setActiveTab('pending_payment')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">{counts.pending_payment}</p>
            <p className="text-sm text-purple-700">Awaiting Payment</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md bg-green-50" onClick={() => setActiveTab('received')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{counts.received}</p>
            <p className="text-sm text-green-700">Received</p>
          </CardContent>
        </Card>
      </div>

      {/* Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText size={20} />
            Shop Orders
            <span className="text-sm font-normal text-slate-500">
              ({filteredOrders.length} orders)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Order #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Supplier</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Date</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Items</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Total</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Created By</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredOrders.map(order => (
                  <tr key={order.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-sm font-medium">{order.orderNumber}</td>
                    <td className="px-4 py-3 font-medium">{order.supplierName}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(order.orderDate)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-slate-100 rounded-full text-xs">
                        {order.items?.length || 0} items
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-bold">{formatCurrency(order.totalAmount)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{order.createdBy}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-center flex-wrap">
                        {/* Approval Actions */}
                        {order.status === 'pending_approval' && canApproveOrders && (
                          <>
                            <Button 
                              size="sm" 
                              onClick={() => handleApproveOrder(order.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Approve
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => { setSelectedOrder(order); setShowRejectDialog(true); }}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <XCircle size={14} className="mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        
                        {/* Payment Action */}
                        {(order.status === 'approved' || order.status === 'pending_payment') && canManagePayment && (
                          <Button 
                            size="sm" 
                            onClick={() => { 
                              setSelectedOrder(order); 
                              setPaymentForm({...paymentForm, amount: order.totalAmount});
                              setShowPaymentDialog(true); 
                            }}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <CreditCard size={14} className="mr-1" />
                            Pay
                          </Button>
                        )}
                        
                        {/* Receive Action */}
                        {order.status === 'paid' && canReceiveOrders && (
                          <Button 
                            size="sm" 
                            onClick={() => { 
                              setSelectedOrder(order); 
                              setReceiveForm({ receivedItems: order.items || [], qualityNotes: '' });
                              setShowReceiveDialog(true); 
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Package size={14} className="mr-1" />
                            Receive
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredOrders.length === 0 && (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                      No orders found. Click "Create Order" to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Order Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="text-emerald-600" size={20} />
              Create Shop Supplier Order
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateOrder} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Supplier Name *</Label>
                <Input
                  value={orderForm.supplierName}
                  onChange={e => setOrderForm({...orderForm, supplierName: e.target.value})}
                  placeholder="Enter supplier name"
                  required
                />
              </div>
              <div>
                <Label>Order Date *</Label>
                <Input
                  type="date"
                  value={orderForm.orderDate}
                  onChange={e => setOrderForm({...orderForm, orderDate: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Expected Delivery</Label>
                <Input
                  type="date"
                  value={orderForm.expectedDelivery}
                  onChange={e => setOrderForm({...orderForm, expectedDelivery: e.target.value})}
                />
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select
                  value={orderForm.paymentTerms}
                  onValueChange={v => setOrderForm({...orderForm, paymentTerms: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cod">Cash on Delivery</SelectItem>
                    <SelectItem value="prepaid">Prepaid</SelectItem>
                    <SelectItem value="check">Check Payment</SelectItem>
                    <SelectItem value="credit_7">7 Days Credit</SelectItem>
                    <SelectItem value="credit_15">15 Days Credit</SelectItem>
                    <SelectItem value="credit_30">30 Days Credit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Add Items */}
            <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
              <h4 className="font-semibold flex items-center gap-2">
                <Package size={16} />
                Order Items
              </h4>
              
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4">
                  <Label>Product</Label>
                  <Select
                    value={newItem.productId}
                    onValueChange={v => {
                      const product = products.find(p => p.id === v);
                      setNewItem({
                        ...newItem,
                        productId: v,
                        productName: product?.name || '',
                        unitPrice: product?.purchasePricePerSack || ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Qty</Label>
                  <Input
                    type="number"
                    value={newItem.quantity}
                    onChange={e => setNewItem({...newItem, quantity: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Unit</Label>
                  <Select
                    value={newItem.unit}
                    onValueChange={v => setNewItem({...newItem, unit: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sack">Sack</SelectItem>
                      <SelectItem value="box">Box</SelectItem>
                      <SelectItem value="pcs">Pieces</SelectItem>
                      <SelectItem value="pack">Pack</SelectItem>
                      <SelectItem value="bottle">Bottle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Unit Price</Label>
                  <Input
                    type="number"
                    value={newItem.unitPrice}
                    onChange={e => setNewItem({...newItem, unitPrice: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div className="col-span-2">
                  <Button type="button" onClick={addItemToOrder} className="w-full bg-emerald-600">
                    <Plus size={16} />
                  </Button>
                </div>
              </div>

              {/* Items List */}
              {orderForm.items.length > 0 && (
                <div className="border rounded-lg overflow-hidden bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-emerald-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Product</th>
                        <th className="px-3 py-2 text-center">Qty</th>
                        <th className="px-3 py-2 text-center">Unit</th>
                        <th className="px-3 py-2 text-right">Price</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {orderForm.items.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">{item.productName}</td>
                          <td className="px-3 py-2 text-center">{item.quantity}</td>
                          <td className="px-3 py-2 text-center">{item.unit}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(item.unitPrice)}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.totalPrice)}</td>
                          <td className="px-3 py-2 text-center">
                            <button 
                              type="button" 
                              onClick={() => removeItemFromOrder(idx)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-emerald-50">
                      <tr>
                        <td colSpan="4" className="px-3 py-2 text-right font-semibold">Total:</td>
                        <td className="px-3 py-2 text-right font-bold text-emerald-600 text-lg">
                          {formatCurrency(calculateOrderTotal())}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={orderForm.notes}
                onChange={e => setOrderForm({...orderForm, notes: e.target.value})}
                placeholder="Additional notes for this order"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Create Order
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="text-purple-600" size={20} />
              Record Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-700">
                <strong>Order:</strong> {selectedOrder?.orderNumber}
              </p>
              <p className="text-sm text-purple-700">
                <strong>Supplier:</strong> {selectedOrder?.supplierName}
              </p>
              <p className="text-lg font-bold text-purple-800 mt-2">
                Total: {formatCurrency(selectedOrder?.totalAmount)}
              </p>
            </div>

            <div>
              <Label>Payment Amount *</Label>
              <Input
                type="number"
                value={paymentForm.amount}
                onChange={e => setPaymentForm({...paymentForm, amount: e.target.value})}
                placeholder="0.00"
                className="text-lg font-semibold"
              />
            </div>

            <div>
              <Label>Payment Method</Label>
              <Select
                value={paymentForm.paymentMethod}
                onValueChange={v => setPaymentForm({...paymentForm, paymentMethod: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="gcash">GCash</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {paymentForm.paymentMethod === 'check' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Check Number</Label>
                  <Input
                    value={paymentForm.checkNumber}
                    onChange={e => setPaymentForm({...paymentForm, checkNumber: e.target.value})}
                    placeholder="Check #"
                  />
                </div>
                <div>
                  <Label>Check Date</Label>
                  <Input
                    type="date"
                    value={paymentForm.checkDate}
                    onChange={e => setPaymentForm({...paymentForm, checkDate: e.target.value})}
                  />
                </div>
              </div>
            )}

            <div>
              <Label>Notes</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={e => setPaymentForm({...paymentForm, notes: e.target.value})}
                placeholder="Payment notes"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreatePayment} className="bg-purple-600 hover:bg-purple-700">
                Record Payment
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="text-blue-600" size={20} />
              Receive Order: {selectedOrder?.orderNumber}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Supplier:</strong> {selectedOrder?.supplierName}
              </p>
              <p className="text-sm text-blue-700">
                <strong>Items will be added to NAS Shop inventory</strong>
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Items to Receive</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-center">Ordered</th>
                      <th className="px-3 py-2 text-center">Unit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {receiveForm.receivedItems.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2">{item.productName}</td>
                        <td className="px-3 py-2 text-center font-medium">{item.quantity}</td>
                        <td className="px-3 py-2 text-center">{item.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <Label>Quality/Receiving Notes</Label>
              <Textarea
                value={receiveForm.qualityNotes}
                onChange={e => setReceiveForm({...receiveForm, qualityNotes: e.target.value})}
                placeholder="Any issues with quantity or quality?"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowReceiveDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleReceiveOrder} className="bg-green-600 hover:bg-green-700">
                <CheckCircle size={16} className="mr-2" />
                Confirm Receipt
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600">
              Are you sure you want to reject order <strong>{selectedOrder?.orderNumber}</strong>?
            </p>
            <div>
              <Label>Reason for Rejection</Label>
              <Textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection"
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowRejectDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleRejectOrder} className="bg-red-600 hover:bg-red-700">
                <XCircle size={16} className="mr-2" />
                Reject Order
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASShopOrders;
